import { useState, useRef, useEffect } from 'react';
import { Loader2, Send, Check, ArrowRight, MessageCircle } from 'lucide-react';
import { storyboardApi } from './storyboardApi';

const CONCEPT_SECTIONS = [
  { key: 'creative_concept', label: 'Creative concept' },
  { key: 'story', label: 'Story' },
  { key: 'narrative_structure', label: 'Narrative structure' },
  { key: 'visual_style', label: 'Visual style' },
];

export default function ConceptPanel({ board, messages, authHeader, onMessagesChange, onBoardChange, onGoToScenes }) {
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState('');
  const chatEndRef = useRef(null);
  const concept = board.concept || {};
  const approved = board.status === 'ready' || board.status === 'generating' || board.status === 'complete';

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages.length, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setError('');
    setInput('');
    onMessagesChange([...messages, { id: `tmp-${Date.now()}`, role: 'user', content: text }]);
    setSending(true);
    const { ok, data } = await storyboardApi.chat(board.id, text, authHeader);
    setSending(false);
    if (ok) {
      onMessagesChange([
        ...messages,
        { id: `u-${Date.now()}`, role: 'user', content: text },
        { id: `a-${Date.now()}`, role: 'assistant', content: data.message.content },
      ]);
      if (data.concept_updated && data.concept) {
        onBoardChange({ ...board, concept: data.concept, status: 'ideating' });
      }
    } else {
      setError(typeof data?.detail === 'string' ? data.detail : 'Message failed');
    }
  };

  const approve = async () => {
    setError('');
    setApproving(true);
    const { ok, data } = await storyboardApi.approve(board.id, authHeader);
    setApproving(false);
    if (ok) onBoardChange({ ...board, status: 'ready' });
    else setError(typeof data?.detail === 'string' ? data.detail : 'Approval failed');
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="sb-concept-panel">
      {/* Left: concept document */}
      <div className="rounded-2xl border border-gray-200/50 bg-white/40 p-5 space-y-4">
        {concept.title && (
          <h4 className="text-base font-semibold text-gray-900" data-testid="sb-concept-title">{concept.title}</h4>
        )}
        {CONCEPT_SECTIONS.map(s => concept[s.key] ? (
          <div key={s.key}>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">{s.label}</p>
            <p className="text-sm text-gray-700 leading-relaxed">{concept[s.key]}</p>
          </div>
        ) : null)}
        {Array.isArray(concept.suggested_scenes) && concept.suggested_scenes.length > 0 && (
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-2">
              Suggested scenes · {concept.suggested_scenes.length}
            </p>
            <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
              {concept.suggested_scenes.map((s, i) => (
                <div key={i} className="flex gap-2.5 text-sm">
                  <span className="text-[10px] text-gray-400 font-mono pt-0.5 w-5 shrink-0">{String(i + 1).padStart(2, '0')}</span>
                  <div>
                    <span className="text-gray-900 font-medium">{s.title}</span>
                    {s.description && <span className="text-gray-500"> — {s.description}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="pt-2 border-t border-gray-200/50 flex items-center gap-2.5 flex-wrap">
          {approved ? (
            <>
              <span className="flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 px-3 py-1.5 rounded-full" data-testid="sb-approved-badge">
                <Check className="w-3.5 h-3.5" /> Concept approved
              </span>
              <button
                data-testid="sb-goto-scenes"
                onClick={onGoToScenes}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-medium bg-gray-900 text-white hover:bg-gray-800 transition-all"
              >
                Go to scenes <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </>
          ) : (
            <>
              <button
                data-testid="sb-approve-btn"
                onClick={approve}
                disabled={approving}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-medium bg-gray-900 text-white hover:bg-gray-800 transition-all active:scale-[0.98] disabled:opacity-50"
              >
                {approving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Approve concept
              </button>
              <span className="text-[11px] text-gray-400">Approving unlocks scene generation.</span>
            </>
          )}
        </div>
        {error && <p className="text-xs text-red-500" data-testid="sb-concept-error">{error}</p>}
      </div>

      {/* Right: refinement chat */}
      <div className="rounded-2xl border border-gray-200/50 bg-white/30 flex flex-col min-h-[420px] max-h-[560px]">
        <div className="px-4 py-3 border-b border-gray-200/50 flex items-center gap-2">
          <MessageCircle className="w-3.5 h-3.5 text-gray-400" />
          <p className="text-xs font-medium text-gray-600">Refine with your creative director</p>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="sb-chat-thread">
          {messages.length === 0 && (
            <p className="text-xs text-gray-400 text-center py-6">
              Ask for changes — tone, pacing, characters, scenes. The concept updates live.
            </p>
          )}
          {messages.map((m, i) => (
            <div key={m.id || i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === 'user'
                  ? 'bg-gray-900 text-white rounded-br-md'
                  : 'bg-white/70 border border-gray-200/50 text-gray-700 rounded-bl-md'
              }`}>
                {m.content}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-white/70 border border-gray-200/50 px-3.5 py-2.5 rounded-2xl rounded-bl-md">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>
        <div className="p-3 border-t border-gray-200/50 flex items-end gap-2">
          <textarea
            data-testid="sb-chat-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="e.g., Make the hook more dramatic..."
            rows={1}
            className="flex-1 px-3.5 py-2.5 rounded-xl bg-white/60 border border-gray-200/50 text-sm text-gray-900 outline-none focus:border-gray-300 placeholder:text-gray-400 resize-none"
          />
          <button
            data-testid="sb-chat-send"
            onClick={send}
            disabled={sending || !input.trim()}
            className="p-2.5 rounded-xl bg-gray-900 text-white hover:bg-gray-800 transition-all disabled:opacity-40"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
